import { normalizePropertyDetail, normalizePropertyListRow } from "@/lib/propertyDisplay";
import {
  contactPropertyListScope,
  normalizeContactPropertyListFilter,
} from "@/lib/contactPropertyListScope";
import { apiClient, clean } from "./apiClient";
import { contactsService } from "./contactsService";
import {
  buildPropertyFileNotePayload,
  noteEntryToPreserve,
  notesService,
  resolveNoteBranchIds,
  resolvePropertyOwnerContactRefs,
} from "./notesService";
import { usersService } from "./usersService";
import { useSettingsStore } from "@/store/settingsStore";
import type { PageRequest, PageResponse } from "@/types/api";
import type { ContactNoteEntry } from "@/types/contact";
import type { PropertyDetail, PropertyFilter, PropertySummary } from "@/types/entities";
import { Property } from "@/types/property";

export interface LeaseTenureTypeOption {
  label: string;
  value: string;
}

const DEFAULT_LIST_PAGE: PageRequest = {
  page: 0,
  size: 20,
  sort: "atlasModifiedAt",
  direction: "desc",
};

function propertyListParams(filter: PropertyFilter, page: PageRequest) {
  const {
    skipGlobalBranchId: _skip,
    skipGlobalContext,
    ...rest
  } = filter as PropertyFilter & {
    skipGlobalBranchId?: boolean;
    skipGlobalContext?: boolean;
  };
  return {
    ...clean(normalizeContactPropertyListFilter(rest)),
    ...clean(page),
    // Must reach the axios interceptor — it strips this flag and, when set,
    // skips injecting the header account's legalEntityId/branchId.
    ...(skipGlobalContext ? { skipGlobalContext: true } : {}),
  };
}

export const propertiesService = {
  async list(filter: PropertyFilter, page: PageRequest): Promise<PageResponse<PropertySummary>> {
    const pageParams: PageRequest = {
      page: page.page ?? DEFAULT_LIST_PAGE.page,
      size: page.size ?? DEFAULT_LIST_PAGE.size,
      sort: page.sort ?? DEFAULT_LIST_PAGE.sort,
      direction: page.direction ?? DEFAULT_LIST_PAGE.direction,
    };
    const pageData = await apiClient
      .get<PageResponse<PropertySummary>>("/properties", {
        params: propertyListParams(filter, pageParams),
      })
      .then((r) => r.data);
    return {
      ...pageData,
      content: (pageData.content ?? []).map((row) => normalizePropertyListRow(row)),
    };
  },

  /**
   * Properties by id — `POST /api/ui/v1/properties/list`. Used to resolve a
   * batch of ids into display names in one round trip; the response has been
   * seen as a bare array and as a paged envelope, so both are accepted.
   */
  async listByIds(ids: string[]): Promise<PropertySummary[]> {
    if (ids.length === 0) return [];
    const data = await apiClient
      .post<PropertySummary[] | PageResponse<PropertySummary>>("/properties/list", { ids })
      .then((r) => r.data);
    const rows = Array.isArray(data) ? data : data?.content ?? [];
    return rows.map((row) => normalizePropertyListRow(row));
  },

  /** Full property — `GET /api/ui/v1/properties/{id}` */
  getOne(id: string): Promise<PropertyDetail> {
    return apiClient
      .get<PropertyDetail>(`/properties/${id}`)
      .then((r) => normalizePropertyDetail(r.data));
  },

  update: async (id: string, body: Record<string, unknown>) => {
    const current = await propertiesService.getOne(id);
    const merged = { ...current, ...body };
    const response = await apiClient.patch<PropertyDetail>(`/properties/${id}`, merged);
    return normalizePropertyDetail(response.data);
  },

  remove(id: string) {
    return apiClient.delete(`/properties/${id}`);
  },
  create(body: Property) {
    return apiClient.post(`/properties`, body);
  },
  resetAvailability(id: string) {
    return apiClient.delete(`/properties/${id}/availability/manual`);
  },

  getLeaseTenureTypes(): Promise<LeaseTenureTypeOption[]> {
    return apiClient
      .get<LeaseTenureTypeOption[]>("/properties/lease-tenure-types")
      .then((r) => r.data);
  },

  listFileNotes(propertyId: string): Promise<ContactNoteEntry[]> {
    return notesService.listForProperty(propertyId);
  },

  async addPropertyFileNote(
    propertyId: string,
    note: { title: string; notes: string },
  ): Promise<ContactNoteEntry[]> {
    const property = await propertiesService.getOne(propertyId);
    const sourceCrm = property.sourceCrm?.trim();
    if (!sourceCrm) {
      throw new Error("Property sourceCrm is required to create a file note");
    }
    const legalEntityId = property.legalEntityId?.trim();
    if (!legalEntityId) {
      throw new Error("Property legalEntityId is required to create a file note");
    }

    const propertyRecord = property as PropertyDetail & { branchId?: string | string[] | null };
    const { legalEntityId: settingsLeId, branchId: settingsBranchId } =
      useSettingsStore.getState();
    const contactsPage = await contactsService.list(
      contactPropertyListScope(settingsLeId, settingsBranchId),
      { page: 0, size: 200 },
    );
    const contactRefs = resolvePropertyOwnerContactRefs(
      property,
      contactsPage.content ?? [],
    );
    const author = await usersService.resolvePropertyUserAuthor(property);

    const payload = buildPropertyFileNotePayload({
      sourceCrm,
      legalEntityId,
      title: note.title,
      notes: note.notes,
      propertyId,
      propertyExternalId: property.externalId ?? null,
      contacts: contactRefs.length > 0 ? contactRefs : null,
      externalAuthorId: author.externalAuthorId,
      externalAuthorName: author.externalAuthorName,
      branchId:
        resolveNoteBranchIds(settingsBranchId, propertyRecord.branchId)?.[0] ?? null,
    });

    await notesService.create(payload);

    return notesService.listForProperty(propertyId);
  },

  async updatePropertyFileNote(
    propertyId: string,
    noteId: string,
    note: { title: string; notes: string },
    existingNote: ContactNoteEntry,
  ): Promise<ContactNoteEntry[]> {
    const property = await propertiesService.getOne(propertyId);
    const sourceCrm = (existingNote.sourceCrm ?? property.sourceCrm)?.trim();
    const legalEntityId = (existingNote.legalEntityId ?? property.legalEntityId)?.trim();
    if (!sourceCrm) {
      throw new Error("sourceCrm is required to update a file note");
    }
    if (!legalEntityId) {
      throw new Error("legalEntityId is required to update a file note");
    }
    const noteExternalId = existingNote.externalId?.trim() || existingNote.id?.trim() || null;
    if (!noteExternalId) {
      throw new Error("Note externalId is required to update a file note");
    }

    const propertyRecord = property as PropertyDetail & { branchId?: string | string[] | null };
    const payload = buildPropertyFileNotePayload({
      sourceCrm,
      legalEntityId,
      title: note.title,
      notes: note.notes,
      propertyId,
      propertyExternalId: property.externalId ?? null,
      branchId:
        resolveNoteBranchIds(useSettingsStore.getState().branchId, propertyRecord.branchId)?.[0] ??
        (Array.isArray(existingNote.branchId)
          ? existingNote.branchId[0]
          : typeof existingNote.branchId === "string"
            ? existingNote.branchId
            : null),
      preserve: noteEntryToPreserve({ ...existingNote, externalId: noteExternalId }),
    });

    await notesService.update(noteId, payload);

    return notesService.listForProperty(propertyId);
  },
};